import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/services/apiClient";

export interface Obligation {
  id: string;
  clientId: string;
  obligationType: string;
  frequency: string;
  lastCompletedAt: string | null;
  nextDueAt: string | null;
  autoCompletable: boolean;
  autoCompleteSignal: string | null;
  actionUrlTemplate: string | null;
  createdAt: string | null;
}

export interface Completion {
  id: string;
  completedAt: string | null;
  completedBy: string;
  completionType: string;
  notes: string | null;
}

interface ObligationsResponse {
  obligations: Obligation[];
}

interface CompletionHistoryResponse {
  completions: Completion[];
}

interface CompleteResponse {
  success: boolean;
  nextDueAt: string | null;
  completionId: string;
}

interface CreateResponse {
  success: boolean;
  obligation: Obligation;
}

interface UpdateResponse {
  success: boolean;
  obligation: Obligation;
}

interface UseObligationsOptions {
  clientId: string;
}

export function useObligations({ clientId }: UseObligationsOptions) {
  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchObligations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<ObligationsResponse>(
        `/api/clients/${clientId}/obligations`
      );
      setObligations(response.obligations || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load obligations"
      );
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchObligations();
  }, [fetchObligations]);

  const createObligation = useCallback(
    async (data: {
      obligationType: string;
      frequency: string;
      nextDueAt: string;
    }) => {
      const response = await apiClient.post<CreateResponse>(
        `/api/clients/${clientId}/obligations`,
        data
      );
      if (response.obligation) {
        setObligations((prev) => [...prev, response.obligation]);
      }
      return response.obligation;
    },
    [clientId]
  );

  const updateObligation = useCallback(
    async (
      obligationId: string,
      data: {
        obligationType?: string;
        frequency?: string;
        nextDueAt?: string;
      }
    ) => {
      const response = await apiClient.put<UpdateResponse>(
        `/api/clients/${clientId}/obligations/${obligationId}`,
        data
      );
      if (response.obligation) {
        setObligations((prev) =>
          prev.map((o) =>
            o.id === obligationId ? response.obligation : o
          )
        );
      }
      return response.obligation;
    },
    [clientId]
  );

  const deleteObligation = useCallback(
    async (obligationId: string) => {
      await apiClient.delete(`/api/clients/${clientId}/obligations/${obligationId}`);
      setObligations((prev) => prev.filter((o) => o.id !== obligationId));
    },
    [clientId]
  );

  const completeObligation = useCallback(
    async (obligationId: string, notes?: string) => {
      const response = await apiClient.post<CompleteResponse>(
        `/api/clients/${clientId}/obligations/${obligationId}/complete`,
        {
          completionType: "manual",
          notes,
        }
      );
      if (response.success && response.nextDueAt) {
        setObligations((prev) =>
          prev.map((o) =>
            o.id === obligationId
              ? {
                  ...o,
                  nextDueAt: response.nextDueAt,
                  lastCompletedAt: new Date().toISOString(),
                }
              : o
          )
        );
      }
      return response;
    },
    [clientId]
  );

  const getHistory = useCallback(
    async (obligationId: string) => {
      const response = await apiClient.get<CompletionHistoryResponse>(
        `/api/clients/${clientId}/obligations/${obligationId}/history`
      );
      return response.completions || [];
    },
    [clientId]
  );

  return {
    obligations,
    loading,
    error,
    refresh: fetchObligations,
    createObligation,
    updateObligation,
    deleteObligation,
    completeObligation,
    getHistory,
  };
}
